import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
    Container,
    Grid,
    Paper,
    Typography,
    TextField,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Box,
    CircularProgress,
    Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Chip } from '@mui/material';
import flaskServer from '../config/flaskserver';

const DicionarioIA = () => {
    const [dicionario, setDicionario] = useState([]);
    const [chatHistory, setChatHistory] = useState([
        {
            sender: 'ia',
            text: 'Olá! Sou seu assistente de Inteligência Artificial. Diga-me qual palavra ou fragmento devo ignorar ao extrair a Razão Social das planilhas. (Ex: "Pare de extrair a palavra BOLETO")',
        },
    ]);
    const [inputMessage, setInputMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const chatEndRef = useRef(null);

    const fetchDicionario = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${flaskServer.flaskServerIP}/dicionario`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.status === 'success') {
                setDicionario(response.data.dicionario);
            }
        } catch (error) {
            console.error('Erro ao buscar dicionário', error);
        }
    };

    useEffect(() => {
        fetchDicionario();
    }, []);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatHistory]);

    const handleSendMessage = async () => {
        if (!inputMessage.trim()) return;

        const userMsg = inputMessage;
        setInputMessage('');
        setChatHistory((prev) => [...prev, { sender: 'user', text: userMsg }]);
        setLoading(true);

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${flaskServer.flaskServerIP}/dicionario/chat`,
                { mensagem: userMsg },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.status === 'success') {
                setChatHistory((prev) => [
                    ...prev,
                    { sender: 'ia', text: response.data.resposta },
                ]);
                if (response.data.termo_adicionado) {
                    setDicionario(response.data.dicionario);
                }
            } else {
                setChatHistory((prev) => [
                    ...prev,
                    { sender: 'ia', text: response.data.message || 'Erro de comunicação com a IA.' },
                ]);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Desculpe, ocorreu um erro genérico na rede ao conectar com a IA.';
            setChatHistory((prev) => [
                ...prev,
                { sender: 'ia', text: errorMsg },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(`${flaskServer.flaskServerIP}/dicionario/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.status === 'success') {
                fetchDicionario();
            }
        } catch (error) {
            console.error('Erro ao remover termo', error);
        }
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold', color: '#1a237e' }}>
                <AutoAwesomeIcon color="primary" fontSize="large" />
                Dicionário Inteligente (I.A)
            </Typography>
            <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                Ensine a Inteligência Artificial a melhorar as extrações higienizando nomenclaturas de Razões Sociais personalizadas.
            </Typography>

            <Grid container spacing={3} sx={{ mt: 2 }}>
                {/* Chatbot Column */}
                <Grid item xs={12} md={7}>
                    <Paper elevation={3} sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '65vh', borderRadius: 3, background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)' }}>
                        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {chatHistory.map((msg, idx) => (
                                <Box key={idx} sx={{ display: 'flex', justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start' }}>
                                    <Paper
                                        elevation={1}
                                        sx={{
                                            p: 2,
                                            maxWidth: '80%',
                                            borderRadius: msg.sender === 'user' ? '20px 20px 0px 20px' : '20px 20px 20px 0px',
                                            backgroundColor: msg.sender === 'user' ? '#1976d2' : '#f5f5f5',
                                            color: msg.sender === 'user' ? 'white' : 'black',
                                        }}
                                    >
                                        <Typography variant="body1">{msg.text}</Typography>
                                    </Paper>
                                </Box>
                            ))}
                            {loading && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                    <CircularProgress size={24} />
                                </Box>
                            )}
                            <div ref={chatEndRef} />
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Exemplo: Pare de considerar a palavra TARIFA..."
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '25px' } }}
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSendMessage}
                                disabled={loading || !inputMessage.trim()}
                                sx={{ borderRadius: '25px', minWidth: '50px', p: 1.5 }}
                            >
                                <SendIcon />
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                {/* Dictionary List Column */}
                <Grid item xs={12} md={5}>
                    <Paper elevation={3} sx={{ p: 3, height: '65vh', display: 'flex', flexDirection: 'column', borderRadius: 3 }}>
                        <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ color: '#1a237e' }}>
                            Regras de Higienização
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Estas regras serão aplicadas automaticamente para limpar as Razões Sociais extraídas dos seus relatórios.
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                            {dicionario.length === 0 ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', color: 'gray' }}>
                                    <AutoAwesomeIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                                    <Typography>Nenhum termo bloqueado ainda.</Typography>
                                    <Typography variant="body2">Converse com a I.A. para adicionar.</Typography>
                                </Box>
                            ) : (
                                <List>
                                    {dicionario.map((item) => {
                                        const getRegraLabel = (tipo) => {
                                            const labels = {
                                                'BANIR': { text: 'Banir Termo', color: 'error' },
                                                'NAO_INICIAR_COM': { text: 'Não iniciar com', color: 'warning' },
                                                'NAO_INICIAR_NUMEROS': { text: 'Remover números iniciais', color: 'info' },
                                                'INICIAR_MAIUSCULA': { text: 'Iniciar na Maiúscula', color: 'primary' },
                                                'INICIAR_APOS_ULTIMO_NUMERO': { text: 'Após último número', color: 'secondary' },
                                                'INICIAR_ANTES_ULTIMO_NUMERO': { text: 'Até último número', color: 'secondary' },
                                                'NAO_CONTER': { text: 'Não pode conter', color: 'error' }
                                            };
                                            return labels[tipo] || { text: tipo, color: 'default' };
                                        };
                                        const labelInfo = getRegraLabel(item.tipo_regra);

                                        return (
                                            <ListItem key={item.id} divider>
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography fontWeight="bold">{item.termo || '(Sem termo)'}</Typography>
                                                            <Chip label={labelInfo.text} size="small" color={labelInfo.color} variant="outlined" />
                                                        </Box>
                                                    }
                                                    secondary={`Adicionado em: ${new Date(item.criado_em).toLocaleDateString()} às ${new Date(item.criado_em).toLocaleTimeString()}`}
                                                />
                                                <ListItemSecondaryAction>
                                                    <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(item.id)} color="error">
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            )}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

export default DicionarioIA;
